import React, { useEffect, useRef, useState } from "react";

import {
    Button,
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Spinner
} from "reactstrap";

import { loadStax } from "./Stax";

import useInitiateStaxPayment from "../hooks/useInitiateStaxPayment";

// NEW:
// Hook used to get/create the Stax customer for the
// current user's organization wallet.
import useEnsureStaxCustomer from "../hooks/useEnsureStaxCustomer";

import StaxPaymentProcessing from "./StaxPaymentProcessing";
import { useNavigate } from "react-router-dom";
import { useWebSocket } from "context/WebSocketContext";

const StaxCheckoutForm = ({amount,onSuccess,onClose}) => {

    console.log("Stax amount:", amount);

    const staxRef = useRef(null);
    const navigate = useNavigate();

    const {walletData} = useWebSocket();

    const [isLoading, setIsLoading] = useState(true);
    const [isCardComplete, setIsCardComplete] = useState(false);
    const [error, setError] = useState("");

    const [expiryMonth, setExpiryMonth] = useState("");
    const [expiryYear, setExpiryYear] = useState("");

    const [processingPayment, setProcessingPayment] = useState(false);
    const [transactionId, setTransactionId] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    // =========================================================
    // STAX PAYMENT HOOK
    // =========================================================

    const {
        initiateStaxPayment,
        isPaying
    } = useInitiateStaxPayment();


    // =========================================================
    // NEW: STAX CUSTOMER HOOK
    // =========================================================
    //
    // This calls:
    //
    // POST /payments/stax/customer/ensure/
    //
    // The backend will:
    //
    // 1. Check wallet.stax_customer_id
    // 2. Return existing customer if available
    // 3. Otherwise create a Stax customer
    // 4. Save the customer ID to Wallet
    // 5. Return customer_id
    //
    // =========================================================

    const {
        ensureStaxCustomerAsync,
        isEnsuringCustomer
    } = useEnsureStaxCustomer();


    // =========================================================
    // PAYMENT AMOUNT
    // =========================================================

    const paymentAmount = Number(amount || 0);


    // =========================================================
    // INITIALIZE STAX
    // =========================================================

    useEffect(() => {

        let mounted = true;


        const initializeStax = async () => {

            try {

                setIsLoading(true);
                setError("");


                // -------------------------------------------------
                // Load Stax.js
                // -------------------------------------------------

                const StaxJs = await loadStax();


                if (!mounted) {
                    return;
                }


                // -------------------------------------------------
                // Get Web Payments Token
                // -------------------------------------------------

                const webPaymentsToken =
                    process.env.REACT_APP_STAX_WEB_PAYMENTS_TOKEN;


                if (!webPaymentsToken) {

                    throw new Error(
                        "Stax Web Payments Token is not configured"
                    );

                }


                // -------------------------------------------------
                // Create Stax instance
                // -------------------------------------------------

                const stax = new StaxJs(
                    webPaymentsToken,
                    {

                        number: {
                            id: "card-number",
                            placeholder: "0000 0000 0000 0000",
                            style:
                                "height: 30px; width: 100%; font-size: 15px;",
                            type: "text",
                            format: "prettyFormat"
                        },

                        cvv: {
                            id: "card-cvv",
                            placeholder: "CVV",
                            style:
                                "height: 30px; width: 100%; font-size: 15px;",
                            type: "text"
                        }

                    }
                );


                staxRef.current = stax;


                // -------------------------------------------------
                // Card form complete
                // -------------------------------------------------

                stax.on(
                    "card_form_complete",
                    () => {

                        if (mounted) {

                            setIsCardComplete(true);
                            setError("");

                        }

                    }
                );


                // -------------------------------------------------
                // Card form incomplete
                // -------------------------------------------------

                stax.on(
                    "card_form_incomplete",
                    () => {

                        if (mounted) {

                            setIsCardComplete(false);

                        }

                    }
                );


                // -------------------------------------------------
                // Display Stax card form
                // -------------------------------------------------

                await stax.showCardForm();


                if (mounted) {

                    setIsLoading(false);

                }

            } catch (err) {

                console.error(
                    "Stax initialization error:",
                    err
                );


                if (mounted) {

                    setError(
                        err?.message ||
                        "Unable to initialize Stax payment form."
                    );

                    setIsLoading(false);

                }

            }

        };


        initializeStax();


        // -------------------------------------------------
        // Cleanup
        // -------------------------------------------------

        return () => {

            mounted = false;

            staxRef.current = null;

        };

    }, []);


    // =========================================================
    // SUBMIT PAYMENT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        if(isSubmitting || processingPayment){
            return;
        }

        setIsSubmitting(true)
        // -------------------------------------------------
        // Validate amount
        // -------------------------------------------------

        if (!paymentAmount || paymentAmount < 1) {

            setError(
                "Invalid payment amount."
            );

            return;

        }


        // -------------------------------------------------
        // Validate Stax instance
        // -------------------------------------------------

        if (!staxRef.current) {

            setError(
                "Stax payment form is not ready."
            );

            return;

        }


        // -------------------------------------------------
        // Validate card
        // -------------------------------------------------

        if (!isCardComplete) {

            setError(
                "Please enter valid card information."
            );

            return;

        }


        // -------------------------------------------------
        // Validate expiration date
        // -------------------------------------------------

        if (!expiryMonth || !expiryYear) {

            setError(
                "Please enter the card expiration date."
            );

            return;

        }


        setError("");


        try {

            // =================================================
            // NEW: ENSURE STAX CUSTOMER
            // =================================================
            //
            // We do this BEFORE tokenize().
            //
            // This is important because previously Stax.js was
            // trying to create/match a customer during tokenization
            // and was failing with:
            //
            // "The address state field must have a value."
            //
            // Now the backend creates/gets the customer first.
            //
            // =================================================

            console.log(
                "Ensuring Stax customer..."
            );


            const customerResponse =
                await ensureStaxCustomerAsync();


            console.log(
                "Stax customer response:",
                customerResponse
            );


            // -------------------------------------------------
            // Get customer ID
            // -------------------------------------------------

            const customerId =
                customerResponse?.customer_id;


            if (!customerId) {

                throw new Error(
                    "Unable to get Stax customer ID."
                );

            }


            console.log(
                "Stax customer ID:",
                customerId
            );


            // =================================================
            // TOKENIZE CARD
            // =================================================

            const extraDetails = {

                method: "card",

                month: expiryMonth,

                year: expiryYear,

                validate: false,

                match_customer: false,

                // NEW:
                // Tell Stax.js which existing customer this
                // payment method belongs to.
                customer_id: customerId

            };


            console.log(
                "Stax tokenization details:",
                extraDetails
            );


            // -------------------------------------------------
            // Tokenize card
            // -------------------------------------------------

            const paymentMethod =
                await staxRef.current.tokenize(
                    extraDetails
                );


            console.log(
                "Stax payment method:",
                paymentMethod
            );


            // -------------------------------------------------
            // Validate payment method
            // -------------------------------------------------

            if (!paymentMethod?.id) {

                throw new Error(
                    "Stax did not return a payment method ID."
                );

            }


            console.log(
                "Stax payment method ID:",
                paymentMethod.id
            );


            // =================================================
            // INITIATE BACKEND PAYMENT
            // =================================================

            initiateStaxPayment(
                {
                    amount: paymentAmount,

                    payment_method_id:
                        paymentMethod.id
                },
                {

                    // -----------------------------------------
                    // Payment initiated successfully
                    // -----------------------------------------

                    onSuccess: (response) => {

                        console.log(
                            "Stax payment initiated:",
                            response
                        );
                        
                        const newTransactionId =
                            response?.data?.transaction_id;

                        if (!newTransactionId) {
                            setError(
                                "Payment was initiated but transaction ID was not returned."
                            );
                            return;
                        }

                        console.log(
                            "Stax transaction ID:",
                            newTransactionId
                        );
                        console.log("BEFORE processing state:", processingPayment);
                        setTransactionId(newTransactionId);

                        setProcessingPayment(true);
                        console.log("AFTER setProcessingPayment(true)");
                        // if (onSuccess) {

                        //     onSuccess(response);

                        // }

                    },


                    // -----------------------------------------
                    // Backend payment error
                    // -----------------------------------------

                    onError: (err) => {

                        console.error(
                            "Stax backend payment error:",
                            err
                        );


                        setError(
                            err?.message ||
                            "Unable to process payment."
                        );

                        setIsSubmitting(false);

                    }

                }
            );


        } catch (err) {

            console.error(
                "Stax payment error:",
                err
            );


            setError(
                err?.message ||
                "Unable to process payment."
            );

        } finally{
            setIsSubmitting(false);
        }

    };


    // =========================================================
    // MONTH OPTIONS
    // =========================================================

    const monthOptions = Array.from(
        { length: 12 },
        (_, index) => {

            const month =
                String(index + 1).padStart(2, "0");


            return (
                <option
                    key={month}
                    value={month}
                >
                    {month}
                </option>
            );

        }
    );


    // =========================================================
    // YEAR OPTIONS
    // =========================================================

    const currentYear =
        new Date().getFullYear();


    const yearOptions = Array.from(
        { length: 15 },
        (_, index) => {

            const year =
                currentYear + index;


            return (
                <option
                    key={year}
                    value={year}
                >
                    {year}
                </option>
            );

        }
    );

    useEffect(() => {
        if (!processingPayment || !transactionId) {
            return;
        }

        if (!walletData) {
            return;
        }

        console.log("Wallet update received:", walletData);

        if (
            walletData.transaction_id === transactionId
        ) {
            console.log(
                "Stax payment successfully completed."
            );

            setIsSubmitting(false);
            setProcessingPayment(false);

            navigate(`/payment-success/?payment_intent=${transactionId}`);
        }

    }, [
        walletData,
        processingPayment,
        transactionId,
        navigate
    ]);

    useEffect(() => {
        console.log(
            "PROCESSING PAYMENT STATE CHANGED:",
            processingPayment
        );
    }, [processingPayment]);

    // =========================================================
    // RENDER
    // =========================================================

    if(processingPayment){
        return <StaxPaymentProcessing />;
    }
    return (

        <div>

            {/* {processingPayment && (<StaxPaymentProcessing/>)} */}

            {/* =================================================
                PAYMENT SUMMARY
            ================================================== */}

            <Card
                className="mb-4"
                style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px"
                }}
            >

                <CardBody>

                    <div
                        className="d-flex justify-content-between align-items-center"
                    >

                        <div>

                            <div
                                className="text-muted"
                                style={{
                                    fontSize: "13px"
                                }}
                            >
                                Payment Amount
                            </div>


                            <div
                                style={{
                                    fontSize: "28px",
                                    fontWeight: "700"
                                }}
                            >
                                ${paymentAmount.toFixed(2)}
                            </div>

                        </div>


                        <div
                            style={{
                                fontSize: "13px",
                                color: "#6c757d"
                            }}
                        >
                            Stax Secure Payment
                        </div>

                    </div>

                </CardBody>

            </Card>


            {/* =================================================
                PAYMENT FORM
            ================================================== */}

            <Form onSubmit={handleSubmit}>


                {/* =================================================
                    CARD NUMBER
                ================================================== */}

                <FormGroup>

                    <Label>
                        Card Number
                    </Label>


                    <div
                        id="card-number"
                        style={{
                            width: "100%",
                            minHeight: "42px",
                            height: "42px",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "6px 10px",
                            backgroundColor: "#fff"
                        }}
                    />

                </FormGroup>


                {/* =================================================
                    EXPIRATION DATE
                ================================================== */}

                <FormGroup>

                    <Label>
                        Expiration Date
                    </Label>


                    <div className="d-flex gap-2">

                        <select
                            id="card-exp-month"
                            className="form-select"
                            style={{
                                width: "120px"
                            }}
                            value={expiryMonth}
                            onChange={(e) => {

                                setExpiryMonth(
                                    e.target.value
                                );

                                setError("");

                            }}
                        >

                            <option
                                value=""
                                disabled
                            >
                                Month
                            </option>


                            {monthOptions}

                        </select>


                        <select
                            id="card-exp-year"
                            className="form-select"
                            style={{
                                width: "120px"
                            }}
                            value={expiryYear}
                            onChange={(e) => {

                                setExpiryYear(
                                    e.target.value
                                );

                                setError("");

                            }}
                        >

                            <option
                                value=""
                                disabled
                            >
                                Year
                            </option>


                            {yearOptions}

                        </select>

                    </div>

                </FormGroup>


                {/* =================================================
                    CVV
                ================================================== */}

                <FormGroup>

                    <Label>
                        CVV
                    </Label>


                    <div
                        id="card-cvv"
                        style={{
                            width: "120px",
                            minHeight: "42px",
                            height: "42px",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "6px 10px",
                            backgroundColor: "#fff"
                        }}
                    />

                </FormGroup>


                {/* =================================================
                    ERROR
                ================================================== */}

                {error && (

                    <div
                        className="alert alert-danger mt-3"
                    >
                        {error}
                    </div>

                )}


                {/* =================================================
                    PAY BUTTON
                ================================================== */}

                <Button
                    type="submit"

                    disabled={

                        isLoading ||

                        paymentAmount < 1 ||

                        !isCardComplete ||

                        !expiryMonth ||

                        !expiryYear ||

                        isPaying ||

                        // NEW:
                        isEnsuringCustomer ||

                        isSubmitting ||

                        processingPayment

                    }

                    className="w-100 mt-3"

                    style={{
                        backgroundColor: "#00ADF0",
                        color: "white",
                        fontWeight: "bolder",
                        minHeight: "44px"
                    }}
                >

                    {/* =================================================
                        NEW:
                        Include customer creation/loading state
                    ================================================== */}

                    {isSubmitting ||
                    isPaying ||
                    isEnsuringCustomer ? (

                        <>

                            <Spinner
                                size="sm"
                                className="me-2"
                            />


                            {isEnsuringCustomer
                                ? "Preparing secure payment..."
                                : "Processing..."
                            }

                        </>

                    ) : (

                        <>

                            Pay ${paymentAmount.toFixed(2)}

                        </>

                    )}

                </Button>


                {/* =================================================
                    CANCEL
                ================================================== */}

                {onClose && (

                    <Button
                        type="button"
                        color="secondary"
                        outline
                        className="w-100 mt-2"

                        disabled={
                            isPaying ||
                            isEnsuringCustomer
                        }

                        onClick={onClose}
                    >

                        Cancel

                    </Button>

                )}

            </Form>

        </div>

    );

};


export default StaxCheckoutForm;